import React, { useCallback, useEffect, useState } from 'react'
import styles from './navbar.module.scss'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { LG_DESKTOP_BREAKPOINT, TABLET_BREAKPOINT } from 'constants/global'
import useWindowSize from 'hooks/use-windowSize'
import classNames from 'classnames'
import {
  MyAccount,
  MyDetails,
  MyProfile,
  MyWatchlists,
  PrivacyAndConsents,
  SignIn,
  SignInDetails,
  SignOut,
} from './links'
import { CustomEventWtn, MY_SOCIAL_MATCHES_KEY } from 'types/global'
import { ReactComponent as AccountIcon } from 'images/icons/icn_user.svg'
import cx from 'classnames'
import i18n from 'constants/strings'
import { emitMenuOpening, setDisableBodyScroll } from 'utils'
import { createPortal } from 'react-dom'
import useElement from 'hooks/use-element'
import useFocusTrap from 'hooks/use-focus-trap'
import WTNSpinner from 'components/generic/WTNSpinner'

interface MenuLoggedInProps<T extends HTMLElement = HTMLButtonElement> {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  triggerRef?: T | null
  hasPendingMatches: boolean
}

const MenuLoggedIn = ({ isOpen, setIsOpen, triggerRef, hasPendingMatches }: MenuLoggedInProps) => {
  const windowWidth = useWindowSize().width
  const handleClose = useCallback(() => {
    setDisableBodyScroll(false)
    setIsOpen(false)
  }, [setIsOpen])

  const { trapContainerRef } = useFocusTrap({
    isActive: isOpen,
    returnFocusElement: triggerRef ?? undefined,
    onPressEscape: handleClose,
    autoFocusDelay: 100,
  })

  return createPortal(
    <nav
      id='mobile-menu-account'
      className={cx(styles.mobileMenuWrapper, { [styles.isOpen]: isOpen })}
      onClick={handleClose}
      ref={trapContainerRef}
      aria-hidden={!isOpen}
    >
      <ul
        className={styles.mobileMenuContainer}
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <li
          className={classNames(styles.navItem, {
            [styles.withNotificationBadge]: hasPendingMatches,
          })}
        >
          <MyProfile />
          {hasPendingMatches && (
            <span
              className={styles.notificationBadge}
              role='status'
              aria-label='you have pending matches to review'
            ></span>
          )}
        </li>
        {windowWidth && windowWidth < TABLET_BREAKPOINT ? (
          <>
            <li className={styles.navItem}>
              <MyDetails />
            </li>
            <li className={styles.navItem}>
              <PrivacyAndConsents />
            </li>
            <li className={styles.navItem}>
              <SignInDetails />
            </li>
          </>
        ) : (
          <li className={styles.navItem}>
            <MyAccount />
          </li>
        )}
        <li className={styles.navItem}>
          <MyWatchlists />
        </li>
        <li className={cx(styles.navItem, styles.separator)}>
          <SignOut />
        </li>
      </ul>
    </nav>,
    document.getElementById('main-nav-header') as HTMLElement
  )
}
const ACCOUNT_MOBILE_MENU_TYPE = 'account'

const AccountMobileMenuToggle = ({ hasPendingMatches }: { hasPendingMatches: boolean }) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggleOpen = () => {
    setDisableBodyScroll(!isOpen)
    if (!isOpen) emitMenuOpening(ACCOUNT_MOBILE_MENU_TYPE)
    setIsOpen(prev => !prev)
  }
  const windowWidth = useWindowSize().width
  const shouldRender = windowWidth < LG_DESKTOP_BREAKPOINT
  const [menuToggle, menuToggleRef] = useElement<HTMLButtonElement>()
  useEffect(() => {
    const handleMenuOpening = (event: CustomEventInit<{ menuType: string }>) => {
      if (event.detail && event.detail.menuType) {
        const thisType = ACCOUNT_MOBILE_MENU_TYPE
        if (event.detail.menuType !== thisType) setIsOpen(false)
      }
    }
    window.addEventListener(CustomEventWtn.MENU_OPENING, handleMenuOpening)
    return () => {
      window.removeEventListener(CustomEventWtn.MENU_OPENING, handleMenuOpening)
    }
  }, [])

  useEffect(() => {
    if (!shouldRender) setIsOpen(false)
  }, [shouldRender])

  if (!shouldRender) return null

  return (
    <>
      <button
        ref={menuToggleRef}
        onClick={toggleOpen}
        aria-label={i18n.t('strCommonUI.ariaLabel.accountMenu')}
        aria-expanded={isOpen}
        aria-controls='mobile-menu-account'
        className={cx(styles.menuToggle, {
          [styles.isOpen]: isOpen,
          [styles.iconNotificationBadge]: hasPendingMatches && !isOpen,
        })}
      >
        <AccountIcon className={styles.accountIcon} aria-hidden />
        <div className={styles.closeIcon} aria-hidden>
          <span></span>
          <span></span>
        </div>
      </button>
      <MenuLoggedIn
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerRef={menuToggle}
        hasPendingMatches={hasPendingMatches}
      />
    </>
  )
}

const SignInOrAccountMenuMobile = () => {
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn)
  const hasPendingMatches = useSelector(
    (state: AppState) => !!state.socialMatches.totals[MY_SOCIAL_MATCHES_KEY]?.PENDING
  )
  const appReady = useSelector((state: AppState) => state.user.appReady)
  const appError = useSelector((state: AppState) => state.user.appError)

  if (!appReady && !appError) return <WTNSpinner width={26} hideLogo />

  if (isLoggedIn) return <AccountMobileMenuToggle hasPendingMatches={hasPendingMatches} />

  return <SignIn className={styles.signInMobile} />
}

export default SignInOrAccountMenuMobile
